import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const FilterModal = ({ open, onClose, currentFilter, currentEventStateFilter, eventStateOptions, currentAlertStatusFilter, alertStatusOptions, onSave, t }) => {
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedEventStates, setSelectedEventStates] = useState([]);
  const [selectedAlertStatuses, setSelectedAlertStatuses] = useState([]);

  // Update filter when modal opens
  useEffect(() => {
    if (open) {
      // Handle both array and string formats for priority filter
      if (Array.isArray(currentFilter)) {
        setSelectedPriorities(currentFilter);
      } else if (currentFilter && currentFilter !== 'all') {
        setSelectedPriorities([currentFilter]);
      } else {
        setSelectedPriorities([]);
      }

      // Handle event state filter
      if (Array.isArray(currentEventStateFilter)) {
        setSelectedEventStates(currentEventStateFilter);
      } else {
        setSelectedEventStates([]);
      }

      // Handle alert status filter (optional)
      setSelectedAlertStatuses(Array.isArray(currentAlertStatusFilter) ? currentAlertStatusFilter : []);
    }
  }, [open, currentFilter, currentEventStateFilter, currentAlertStatusFilter]);

  const handlePriorityToggle = (priority) => {
    setSelectedPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    );
  };

  const handleEventStateToggle = (stateValue) => {
    setSelectedEventStates(prev =>
      prev.includes(stateValue)
        ? prev.filter(s => s !== stateValue)
        : [...prev, stateValue]
    );
  };

  const handleAlertStatusToggle = (statusValue) => {
    setSelectedAlertStatuses(prev =>
      prev.includes(statusValue)
        ? prev.filter(s => s !== statusValue)
        : [...prev, statusValue]
    );
  };

  const handleSave = () => {
    onSave({
      priorities: selectedPriorities,
      eventStates: selectedEventStates,
      alertStatuses: selectedAlertStatuses
    });
    onClose();
  };

  const handleClear = () => {
    setSelectedPriorities([]);
    setSelectedEventStates([]);
    setSelectedAlertStatuses([]);
  };

  const hasAnyFilter = selectedPriorities.length > 0 || selectedEventStates.length > 0 || selectedAlertStatuses.length > 0;

  if (!open) return null;

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1300,
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '8px',
      maxWidth: '500px',
      width: '90%',
      padding: '0',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      margin: 0,
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#333',
    },
    activeChip: {
      padding: '4px 12px',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: '16px',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body: {
      padding: '24px',
    },
    filterRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px',
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: 500,
      color: '#333',
      minWidth: '100px',
      marginRight: '20px',
    },
    chipsContainer: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      flex: 1,
    },
    chip: {
      padding: '6px 16px',
      borderRadius: '16px',
      border: '1px solid #e0e0e0',
      backgroundColor: 'white',
      color: '#666',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      userSelect: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '60px',
    },
    chipSelected: {
      backgroundColor: '#1976d2',
      color: 'white',
      borderColor: '#1976d2',
    },
    chipHigh: {
      '&:hover': {
        borderColor: '#d32f2f',
        backgroundColor: '#ffebee',
      }
    },
    chipMedium: {
      '&:hover': {
        borderColor: '#ff9800',
        backgroundColor: '#fff3e0',
      }
    },
    chipLow: {
      '&:hover': {
        borderColor: '#4caf50',
        backgroundColor: '#e8f5e9',
      }
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '0.95rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    clearButton: {
      padding: '6px 16px',
      borderRadius: '16px',
      backgroundColor: 'transparent',
      color: '#666',
      border: '1px solid #e0e0e0',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    clearButtonDisabled: {
      color: '#ccc',
      borderColor: '#eee',
      cursor: 'not-allowed',
    },
    cancelButton: {
      backgroundColor: '#f5f5f5',
      color: '#666',
      marginLeft: '8px',
    },
    saveButton: {
      backgroundColor: '#1976d2',
      color: 'white',
      marginLeft: '8px',
    },
    buttonGroup: {
      display: 'flex',
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#d32f2f';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const getPriorityBackgroundColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ffebee';
      case 'medium':
        return '#fff3e0';
      case 'low':
        return '#e8f5e9';
      default:
        return '#f5f5f5';
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>
            {t?.('filters') || 'Filters'}
          </h2>
          {hasAnyFilter && (
            <span style={modalStyles.activeChip}>
              {selectedPriorities.length + selectedEventStates.length + selectedAlertStatuses.length} {t?.('filter_active') || 'Active'}
            </span>
          )}
        </div>

        <div style={modalStyles.body}>
          <div style={modalStyles.filterRow}>
            <div style={modalStyles.label}>
              {t?.('priority') || 'Priority'}
            </div>
            <div style={modalStyles.chipsContainer}>
              {['high', 'medium', 'low'].map((priority) => {
                const isSelected = selectedPriorities.includes(priority);
                const chipStyle = {
                  ...modalStyles.chip,
                  ...(isSelected ? {
                    backgroundColor: getPriorityColor(priority),
                    color: 'white',
                    borderColor: getPriorityColor(priority),
                  } : {
                    ':hover': {
                      borderColor: getPriorityColor(priority),
                      backgroundColor: getPriorityBackgroundColor(priority),
                    }
                  })
                };

                return (
                  <div
                    key={priority}
                    style={chipStyle}
                    onClick={() => handlePriorityToggle(priority)}
                  >
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </div>
                );
              })}
              <button
                style={{
                  ...modalStyles.clearButton,
                  ...(hasAnyFilter ? {} : modalStyles.clearButtonDisabled),
                }}
                onClick={hasAnyFilter ? handleClear : undefined}
                disabled={!hasAnyFilter}
              >
                {t?.('clear') || 'Clear'}
              </button>
            </div>
          </div>

          {/* Event State Filter */}
          {eventStateOptions?.length > 0 && (
            <div style={modalStyles.filterRow}>
              <div style={modalStyles.label}>
                {t?.('event_state') || 'Event State'}
              </div>
              <div style={modalStyles.chipsContainer}>
                {eventStateOptions.map((option) => (
                  <div
                    key={option.value}
                    style={{
                      ...modalStyles.chip,
                      ...(selectedEventStates.includes(option.value) && modalStyles.chipSelected)
                    }}
                    onClick={() => handleEventStateToggle(option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alert Status Filter (optional) */}
          {alertStatusOptions?.length > 0 && (
            <div style={modalStyles.filterRow}>
              <div style={modalStyles.label}>
                {t?.('alert_status') || 'Alert Status'}
              </div>
              <div style={modalStyles.chipsContainer}>
                {alertStatusOptions.map((option) => (
                  <div
                    key={option.value}
                    style={{
                      ...modalStyles.chip,
                      ...(selectedAlertStatuses.includes(option.value) && modalStyles.chipSelected)
                    }}
                    onClick={() => handleAlertStatusToggle(option.value)}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={modalStyles.footer}>
          <div style={modalStyles.buttonGroup}>
            <button
              style={{
                ...modalStyles.button,
                ...modalStyles.cancelButton,
              }}
              onClick={onClose}
            >
              {t?.('cancel') || 'Cancel'}
            </button>
            <button
              style={{
                ...modalStyles.button,
                ...modalStyles.saveButton,
              }}
              onClick={handleSave}
            >
              {t?.('apply') || 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

FilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentFilter: PropTypes.string,
  currentEventStateFilter: PropTypes.array,
  eventStateOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  currentAlertStatusFilter: PropTypes.array,
  alertStatusOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired
  })),
  onSave: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default FilterModal;